import React from 'react';
import { CheckCircle, UserCheck, FileText } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const KYC = () => {
  return (
    <div className="dark-container min-h-screen pt-28 pb-20">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <div className="w-20 h-20 mx-auto mb-6 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
            <UserCheck className="text-brand-primary" size={36} />
          </div>
          <h1 className="display-large mb-4" style={{ color: 'var(--text-primary)' }}>KYC/AML Policy</h1>
          <p className="body-medium" style={{ color: 'var(--text-muted)' }}>Know Your Customer & Anti-Money Laundering</p>
        </div>

        <Card className="bg-white/5 border border-white/25 backdrop-blur-xl mb-8">
          <CardContent className="p-8">
            <h2 className="heading-1 mb-4" style={{ color: 'var(--text-primary)' }}>1. Purpose</h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              MatrixLaunch is committed to preventing money laundering, terrorist financing, and other financial crimes. Our KYC/AML policy ensures compliance with international regulations including:
            </p>
            <ul className="space-y-2 mb-6" style={{ color: 'var(--text-secondary)' }}>
              <li>• Financial Action Task Force (FATF) recommendations</li>
              <li>• US Bank Secrecy Act (BSA) and PATRIOT Act</li>
              <li>• EU Anti-Money Laundering Directives (AMLD5/6)</li>
              <li>• Local jurisdictional requirements</li>
            </ul>
            
            <h2 className="heading-1 mb-4 mt-8" style={{ color: 'var(--text-primary)' }}>2. KYC Requirements</h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>All users must complete identity verification before investing:</p>
            
            <div className="grid md:grid-cols-3 gap-6 my-6">
              <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-lg p-6">
                <FileText className="text-brand-primary mb-3" size={28} />
                <h3 className="heading-2 mb-2" style={{ color: 'var(--text-primary)' }}>Level 1</h3>
                <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>Basic verification</p>
                <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <li>• Full name</li>
                  <li>• Email address</li>
                  <li>• Phone number</li>
                  <li>• Date of birth</li>
                  <li>Limit: $1,000</li>
                </ul>
              </div>
              
              <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-lg p-6">
                <UserCheck className="text-brand-primary mb-3" size={28} />
                <h3 className="heading-2 mb-2" style={{ color: 'var(--text-primary)' }}>Level 2</h3>
                <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>Enhanced verification</p>
                <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <li>• Government ID</li>
                  <li>• Proof of address</li>
                  <li>• Selfie verification</li>
                  <li>• Source of funds</li>
                  <li>Limit: $50,000</li>
                </ul>
              </div>
              
              <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-lg p-6">
                <CheckCircle className="text-brand-primary mb-3" size={28} />
                <h3 className="heading-2 mb-2" style={{ color: 'var(--text-primary)' }}>Level 3</h3>
                <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>Premium verification</p>
                <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <li>• Video interview</li>
                  <li>• Bank statements</li>
                  <li>• Tax documents</li>
                  <li>• Enhanced due diligence</li>
                  <li>Limit: Unlimited</li>
                </ul>
              </div>
            </div>
            
            <h2 className="heading-1 mb-4 mt-8" style={{ color: 'var(--text-primary)' }}>3. Accepted Documents</h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>Valid government-issued identification:</p>
            <ul className="space-y-2 mb-6" style={{ color: 'var(--text-secondary)' }}>
              <li>• Passport (all pages)</li>
              <li>• National ID card (both sides)</li>
              <li>• Driver's license (both sides)</li>
            </ul>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>Proof of address (issued within 3 months):</p>
            <ul className="space-y-2 mb-6" style={{ color: 'var(--text-secondary)' }}>
              <li>• Utility bill (electricity, water, gas)</li>
              <li>• Bank statement</li>
              <li>• Government correspondence</li>
            </ul>
            
            <h2 className="heading-1 mb-4 mt-8" style={{ color: 'var(--text-primary)' }}>4. AML Procedures</h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>We monitor transactions for suspicious activity including:</p>
            <ul className="space-y-2 mb-6" style={{ color: 'var(--text-secondary)' }}>
              <li>• Unusual transaction patterns or volumes</li>
              <li>• Rapid fund movements</li>
              <li>• Transactions from high-risk jurisdictions</li>
              <li>• Structuring to avoid reporting thresholds</li>
            </ul>
            
            <h2 className="heading-1 mb-4 mt-8" style={{ color: 'var(--text-primary)' }}>5. Restricted Jurisdictions</h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>We do not accept users from:</p>
            <ul className="space-y-2 mb-6" style={{ color: 'var(--text-secondary)' }}>
              <li>• FATF blacklisted countries</li>
              <li>• Countries under UN/EU/US sanctions</li>
              <li>• Jurisdictions prohibiting cryptocurrency activities</li>
            </ul>
            
            <h2 className="heading-1 mb-4 mt-8" style={{ color: 'var(--text-primary)' }}>6. Enhanced Due Diligence</h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>Additional verification may be required for:</p>
            <ul className="space-y-2 mb-6" style={{ color: 'var(--text-secondary)' }}>
              <li>• High-value transactions (>$50,000)</li>
              <li>• Politically Exposed Persons (PEPs)</li>
              <li>• High-risk business activities</li>
              <li>• Unusual transaction patterns</li>
            </ul>
            
            <h2 className="heading-1 mb-4 mt-8" style={{ color: 'var(--text-primary)' }}>7. Data Security</h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              All KYC data is encrypted and stored securely. Access is restricted to authorized compliance personnel only. We partner with certified KYC providers including Onfido and Sumsub.
            </p>
            
            <h2 className="heading-1 mb-4 mt-8" style={{ color: 'var(--text-primary)' }}>8. Reporting</h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              We file Suspicious Activity Reports (SARs) with relevant authorities when required. We cooperate fully with law enforcement investigations.
            </p>
            
            <div className="bg-brand-primary/10 border border-brand-primary rounded-lg p-6 mt-8">
              <h3 className="heading-2 text-brand-primary mb-3">Verification Timeline</h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                • Level 1: Instant verification<br/>
                • Level 2: 1-3 business days<br/>
                • Level 3: 3-7 business days
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default KYC;
